import { Link } from "react-router-dom";
import { MapPin, Flag, Layers, Route, CreditCard, Banknote, QrCode } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHead, BtnOrange, BtnGhost } from "@/components/bits";
import FAQ from "@/components/FAQ";
import { usePreRegister } from "@/components/PreRegisterModal";
import { IMAGES, FAQ_PASSAGEIROS } from "@/data/site";

const STEPS = [
  { icon: Flag, t: "Cadastre-se", d: "Crie sua conta no aplicativo e complete as informações necessárias." },
  { icon: MapPin, t: "Escolha seu destino", d: "Informe o local de partida e o destino desejado." },
  { icon: Layers, t: "Selecione a categoria", d: "Escolha entre as categorias disponíveis, conforme a região." },
  {
    icon: Route,
    t: "Acompanhe sua viagem",
    d: "Visualize as informações da corrida, identifique o motorista e utilize os recursos de segurança e acompanhamento.",
  },
];

const PAGAMENTOS = [
  { icon: QrCode, t: "Pix" },
  { icon: CreditCard, t: "Cartão" },
  { icon: Banknote, t: "Dinheiro" },
];

const SEGURANCA = [
  "Identificação do motorista",
  "Informações do veículo",
  "Código de embarque",
  "Compartilhamento de viagem",
  "Canais de suporte e segurança",
];

export default function Passageiros() {
  const { openPreRegister } = usePreRegister();

  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Para passageiros"
        title="Viaje com mais praticidade pelo 360Taxi."
        lead="O 360Taxi foi desenvolvido para simplificar a mobilidade urbana, oferecendo uma experiência moderna desde a solicitação da corrida até a chegada ao destino."
        image={IMAGES.passengerRide}
        imageAlt="Passageiro acompanhando a viagem pelo aplicativo"
      />

      <section className="mx-auto max-w-8xl px-4 py-20 sm:px-6 md:py-24 lg:px-8" data-testid="como-funciona-passageiros">
        <Reveal>
          <SectionHead eyebrow="Como funciona" title="Quatro passos até o seu destino." />
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08} className="h-full">
              <div className="relative flex h-full flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-7">
                <span className="font-mono text-4xl font-bold text-brand-orange/25">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <s.icon className="h-7 w-7 text-brand-orange" />
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{s.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-24" data-testid="pagamentos-passageiros">
        <div className="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHead
              eyebrow="Pagamentos"
              title="Formas de pagamento do aplicativo."
              lead="As formas de pagamento disponibilizadas pelo aplicativo poderão incluir:"
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {PAGAMENTOS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
                    <p.icon className="h-6 w-6" />
                  </span>
                  <p className="font-display text-xl font-bold text-slate-900">{p.t}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-6 text-sm leading-relaxed text-slate-500">
              As opções efetivamente disponíveis respeitam a configuração da operação e os métodos aceitos para a
              corrida. Apenas meios habilitados serão exibidos no aplicativo.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-8xl px-4 py-20 sm:px-6 md:py-24 lg:px-8" data-testid="seguranca-passageiros">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <SectionHead
              eyebrow="Segurança"
              title="Recursos de segurança da sua viagem."
            />
            <ul className="mt-8 space-y-3">
              {SEGURANCA.map((s) => (
                <li key={s} className="flex items-start gap-3 text-base text-slate-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                  {s}
                </li>
              ))}
            </ul>
            <BtnGhost as={Link} to="/seguranca" dark={false} className="mt-8">
              Página de segurança
            </BtnGhost>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-2xl">
              <img
                src={IMAGES.safetyTech}
                alt="Passageiro e motorista conectados pela tecnologia do 360Taxi"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-24" id="perguntas-frequentes" data-testid="faq-passageiros">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHead eyebrow="Perguntas frequentes" title="Dúvidas de passageiros." />
          </Reveal>
          <Reveal delay={0.1}>
            <FAQ items={FAQ_PASSAGEIROS} idPrefix="faq-passageiros" />
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-8xl px-4 pb-24 pt-16 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-center gap-6 rounded-3xl bg-brand-obsidian px-6 py-14 text-center sm:px-12">
            <h2 className="max-w-xl font-display text-2xl font-extrabold text-white sm:text-3xl">
              Receba um aviso quando o 360Taxi chegar.
            </h2>
            <BtnOrange
              data-testid="passageiros-cta-pre-cadastro"
              onClick={() => openPreRegister({ interesse: "Passageiro 360Taxi", origem: "passageiros" })}
            >
              Quero ser avisado
            </BtnOrange>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
